import { isEmpty } from 'lodash';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
// mui
import { MenuItem, Stack, Skeleton, TextField } from '@mui/material';
// custom component
import {
  NumberInputField,
  PickerInputField,
  SelectInputField,
  SectionLabel,
  MultipleSelectCheckmarks,
} from '@/features/bookings-management/components';
// api
import { getSettingsData } from '@/features/app/api';
//
// hooks + utils
import useResponsive from '@/hooks/useResponsive';
import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';
//
import { consoleError } from '@/utils/consoleError';
// redux
import { useSelector, dispatch } from '@/stores';
import {
  selectService,
  selectVariant,
  selectedVariant,
  setPropertyType,
  setValidationError,
} from '@/features/bookings-management/slices';
// types
import { ServiceVariant, ServiceFormField } from '@/types';

const RECURRING_VARIANT_IDS = [2, 3, 5];

const includesAny = (value: string, keywords: string[]) =>
  keywords.some((keyword) => value.includes(keyword));

const shouldShowRecurrenceDays = (variant: Partial<ServiceVariant>) => {
  if (isEmpty(variant)) return false;

  const recurrence = variant.service_reccurence ?? variant.service_recurrence;
  const recurrenceText = [recurrence?.ref, recurrence?.name]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  if (recurrenceText) {
    if (includesAny(recurrenceText, ['one-off', 'one off', 'once'])) {
      return false;
    }

    return includesAny(recurrenceText, ['weekly', 'fortnightly', 'recurring']);
  }

  const variantText = [variant.ref, variant.name]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return (
    RECURRING_VARIANT_IDS.includes(Number(variant.id)) ||
    includesAny(variantText, [
      'weekly',
      'fortnightly',
      'recurring',
      'office',
      'commercial',
    ])
  );
};

export default function ServiceDetails() {
  const downMd = useResponsive('down', 'md');
  const { bookingId } = useParams();
  const isEditingBooking = Boolean(bookingId);
  const choosenVariant: ServiceVariant = useSelector(selectedVariant);
  const {
    all_variants_from_api: serviceVariantsReduxCopy,
    cleanDetails: { property_type_id },
    selectedServiceId,
    validationErrors,
  } = useSelector((state) => state.booking);
  const { errorMsg } = useSnackbarMsg();
  // const queryClient = useQueryClient();
  // settingsData is fetched in the parent component. Using this instead of the redux version
  // prevents a bug where the settings are not fetched and STORED IN REDUX in time
  // const settingsCachedData = queryClient.getQueryData<Settings>(['settings']);
  // SETTINGS
  const { data: settingsData } = useQuery({
    // TODO: probably this data should be fetched on app load
    queryKey: ['settings'],
    queryFn: getSettingsData,
    onError: (error) => {
      errorMsg(error, `Something went wrong while fetching settings data `);
    },
    refetchOnWindowFocus: false,
  });
  // ---------------------------------------------------
  //                    HELPER FUNCTIONS
  // ---------------------------------------------------
  const handleSelectedService = (serviceId: number) => {
    if (isEditingBooking) return;

    dispatch(selectService(serviceId));
    validationErrors.selectedServiceId &&
      dispatch(setValidationError({ field: 'selectedServiceId', error: null }));
  };
  const handleSelectedVariant = (variantId: number) => {
    // set selected variant
    const currentVariant = serviceVariantsReduxCopy.find(
      (variant) => variant.id === variantId
    );
    if (!currentVariant) return;

    dispatch(
      selectVariant({
        variant: currentVariant,
        preserveSelectedExtrasAndRecurrence: isEditingBooking,
      })
    );
    validationErrors.selectedServiceVariant &&
      dispatch(
        setValidationError({ field: 'selectedServiceVariant', error: null })
      );
  };

  // ---------------------------------------------------
  //        HELPER FUNCTION TO RENDER INPUT FIELDS
  // ---------------------------------------------------

  const renderInputFields = (
    type: string,
    serviceFormField: ServiceFormField
  ) => {
    switch (type) {
      case 'select':
        return <SelectInputField serviceFormField={serviceFormField} />;
      case 'picker':
        return <PickerInputField serviceFormField={serviceFormField} />;
      case 'number':
        return <NumberInputField serviceFormField={serviceFormField} />;

      default:
        consoleError('ServiceDetails.tsx', 'renderInputFields()');
        return <div>There was an error here: {serviceFormField.name}</div>;
    }
  };

  // ---------------------------------------------------
  return (
    <Stack direction="column" spacing={2}>
      <SectionLabel label="Service Details:" />

      {settingsData ? (
        <Stack direction="column" spacing={1} width="100%">
          <TextField
            size="small"
            select
            sx={{ width: downMd ? '100%' : '75%' }}
            label="Service"
            InputLabelProps={{ shrink: true }}
            SelectProps={{ native: false, sx: { textTransform: 'capitalize' } }}
            onChange={(e) => handleSelectedService(Number(e.target.value))}
            value={selectedServiceId ?? ''}
            disabled={isEditingBooking}
            error={!!validationErrors.selectedServiceId}
            helperText={validationErrors.selectedServiceId}
          >
            {settingsData.services.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.name}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      ) : (
        <Skeleton variant="text" width={200} />
      )}
      {/* TODO - check the skeleton logic vor the service type */}
      {selectedServiceId && settingsData && (
        <>
          {settingsData.serviceVariants ? (
            <Stack direction="column" spacing={1} width="100%">
              <TextField
                size="small"
                select
                sx={{ width: downMd ? '100%' : '75%' }}
                label="Service Type"
                InputLabelProps={{ shrink: true }}
                SelectProps={{
                  native: false,
                  sx: { textTransform: 'capitalize' },
                }}
                onChange={(e) => handleSelectedVariant(Number(e.target.value))}
                value={choosenVariant.id ?? ''}
                error={!!validationErrors.selectedServiceVariant}
                helperText={validationErrors.selectedServiceVariant}
              >
                {settingsData.serviceVariants
                  .filter((variant) => variant.service_id === selectedServiceId)
                  .map((variant) => (
                    <MenuItem key={variant.id} value={variant.id}>
                      {variant.name}
                    </MenuItem>
                  ))}
              </TextField>
            </Stack>
          ) : (
            <Skeleton variant="text" width={200} />
          )}

          <Stack direction="column" spacing={2}>
            {/* PROPERTY TYPES DROWDOWN */}
            {choosenVariant.property_type &&
              !!choosenVariant.property_type.length && (
                <TextField
                  select
                  size="small"
                  sx={{ width: downMd ? '100%' : '75%' }}
                  label="Property type"
                  InputLabelProps={{ shrink: true }}
                  SelectProps={{
                    native: false,
                    sx: { textTransform: 'capitalize' },
                  }}
                  value={property_type_id ?? ''}
                  error={!!validationErrors.property_type_id}
                  helperText={validationErrors.property_type_id}
                  onChange={(e) => {
                    dispatch(setPropertyType(e.target.value));
                    dispatch(
                      setValidationError({
                        field: 'property_type_id',
                        error: null,
                      })
                    );
                  }}
                >
                  {choosenVariant.property_type.map((property) => (
                    <MenuItem key={property.id} value={property.id}>
                      {property.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}

            {/* IF RECURRING => SHOW DAY OF WEEK */}
            {shouldShowRecurrenceDays(choosenVariant) && (
              <MultipleSelectCheckmarks />
            )}

            {/* SHOW CONFIGURED SERVICE FORM FIELDS */}
            {!isEmpty(choosenVariant) &&
              choosenVariant.service_form_fields.map((field, index) => (
                <Stack direction="row" spacing={2} key={index}>
                  {renderInputFields(field.type, field)}
                </Stack>
              ))}
          </Stack>
        </>
      )}
    </Stack>
  );
}
